/**
 * Hook personalizado para gerenciar o estado e operações do FluxSigner
 */
import { useState, useEffect, useCallback } from "react";
import fluxSignerService, {
  CertificateInfo,
  CertificateListResponse,
  SignPDFResponse,
  SignPDFOptions,
} from "../../services/fluxsigner/FluxSignerService";

export interface UseFluxSignerReturn {
  // Estado da extensão
  isAvailable: boolean;
  checkingExtension: boolean;
  extensionStatus: { available: boolean; extensionId: string; detectedId?: string };

  // Certificados
  certificates: CertificateInfo[];
  certificatesLoading: boolean;
  certificatesError: string | null;

  // Operações
  loading: boolean;
  error: string | null;

  // Métodos
  loadCertificates: () => Promise<void>;
  signDocumentBase64: (pdfBase64: string, certificateAlias: string, options?: SignPDFOptions) => Promise<SignPDFResponse>;
  clearError: () => void;
  forceCheckExtension: () => Promise<boolean>;
}

export const useFluxSigner = (): UseFluxSignerReturn => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [checkingExtension, setCheckingExtension] = useState(true);
  const [certificates, setCertificates] = useState<CertificateInfo[]>([]);
  const [certificatesLoading, setCertificatesLoading] = useState(false);
  const [certificatesError, setCertificatesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Verificar disponibilidade da extensão
  useEffect(() => {
    const checkAvailability = async () => {
      setCheckingExtension(true);
      const available = await fluxSignerService.checkExtensionAvailability();
      setIsAvailable(available);
      setCheckingExtension(false);
    };
    checkAvailability();
  }, []);

  // Carregar certificados
  const loadCertificates = useCallback(async () => {
    if (!isAvailable) {
      setCertificatesError("FluxSigner não disponível");
      return;
    }

    setCertificatesLoading(true);
    setCertificatesError(null);

    try {
      const result: CertificateListResponse = await fluxSignerService.getCertificates();
      const icpBrasilCerts = result.certificates?.filter((cert) => cert.icpBrasilCompliant) || [];
      setCertificates(icpBrasilCerts);
      console.log(
        `✅ Certificados carregados: ${result.certificates?.length || 0} encontrados, ${
          icpBrasilCerts.length
        } são ICP-Brasil.`,
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Erro desconhecido";
      setCertificatesError(errorMessage);
      console.error("❌ Erro ao carregar certificados:", errorMessage);
    } finally {
      setCertificatesLoading(false);
    }
  }, [isAvailable]);

  // Assinar documento usando base64
  const signDocumentBase64 = useCallback(
    async (pdfBase64: string, certificateAlias: string, options: SignPDFOptions = {}): Promise<SignPDFResponse> => {
      if (!isAvailable) {
        throw new Error("FluxSigner não está disponível");
      }

      if (!pdfBase64) {
        throw new Error("PDF base64 não fornecido");
      }

      if (!certificateAlias) {
        throw new Error("Por favor, selecione um certificado");
      }

      setLoading(true);
      setError(null);

      try {
        console.log("ℹ️ Iniciando assinatura PDF com FluxSigner...");
        const result = await fluxSignerService.signPDFBase64(pdfBase64, certificateAlias, options);
        console.log("✅ Documento assinado com sucesso!");
        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Erro desconhecido";
        setError(errorMessage);
        console.error("❌ Erro na assinatura:", errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [isAvailable],
  );

  // Limpar erro
  const clearError = useCallback(() => {
    setError(null);
    setCertificatesError(null);
  }, []);

  // Forçar verificação da extensão
  const forceCheckExtension = useCallback(async (): Promise<boolean> => {
    try {
      setCheckingExtension(true);
      const result = await fluxSignerService.forceCheckExtension();
      setIsAvailable(result);
      setCheckingExtension(false);
      return result;
    } catch (error) {
      console.error("Erro ao verificar extensão:", error);
      setIsAvailable(false);
      setCheckingExtension(false);
      return false;
    }
  }, []);

  // Obter status da extensão
  const extensionStatus = fluxSignerService.getExtensionStatus();

  return {
    // Estado da extensão
    isAvailable,
    checkingExtension,
    extensionStatus,

    // Certificados
    certificates,
    certificatesLoading,
    certificatesError,

    // Operações
    loading,
    error,

    // Métodos
    loadCertificates,
    signDocumentBase64,
    clearError,
    forceCheckExtension,
  };
};

